// components/DeviceDetail.tsx

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  FaCog as FaCogIcon,
  FaThermometerHalf as FaThermometerHalfIcon,
  FaTint as FaTintIcon,
  FaBolt as FaBoltIcon,
  FaMicrochip as FaMicrochipIcon,
  FaDownload as FaDownloadIcon,
  FaYoutube as FaYoutubeIcon
} from 'react-icons/fa';
import { updateRecord, updateName, fetchDeviceByMac } from './DataHandler';
import './DeviceDetail.css';
import MQTTService from './MQTTService';
import jwt_decode from 'jwt-decode';
import ModalLed from './ModalLed';
import ModalSwitch from './ModalSwitch';
import ModalMeasure from './ModalMeasure';

/* Icon Type Casting to fix build errors */
const FaCog = FaCogIcon as any;
const FaTemp = FaThermometerHalfIcon as any;
const FaHumi = FaTintIcon as any;
const FaBolt = FaBoltIcon as any;
const FaMicrochip = FaMicrochipIcon as any;
const FaDownload = FaDownloadIcon as any;
const FaYoutube = FaYoutubeIcon as any;

interface DeviceDetailProps {
  mqttMessage: any;
}

interface Device {
  mac: string;
  name?: string[];
  temperature?: number;
  humidity?: number;
  in?: number[];
  out?: number[];
  [key: string]: any;
}

const DeviceDetail = ({ mqttMessage }: DeviceDetailProps) => {
  const { mac } = useParams<{ mac: string }>();
  const [device, setDevice] = useState<Device | null>(null);
  const [email, setEmail] = useState<string>('');
  const [editMode, setEditMode] = useState(false);
  const [editedNames, setEditedNames] = useState<string[]>([]);

  // Modals
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTargetIndex, setModalTargetIndex] = useState<number | null>(null);
  const [isSwitchModalOpen, setIsSwitchModalOpen] = useState(false);
  const [selectedSwitchIndex, setSelectedSwitchIndex] = useState<number | null>(null);
  const [editedSwitchLabel, setEditedSwitchLabel] = useState('');

  const [modalState, setModalState] = useState<{ isOpen: boolean; measurement?: { key: string; label: string } }>({
    isOpen: false,
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      const decodedToken = jwt_decode<{ email: string }>(token);
      setEmail(decodedToken.email);
    }
  }, []);

  useEffect(() => {
    if (mac) {
      fetchDeviceByMac(mac)
        .then((data) => {
          setDevice(data);
          setEditedNames(data.name || []);
        })
        .catch((error) => console.error('Error fetching device data:', error));
    }
  }, [mac]);

  useEffect(() => {
    if (mqttMessage && mac) {
      try {
        const parsedMessage = JSON.parse(mqttMessage);
        if (parsedMessage.mac === mac) {
          setDevice((prevDevice) => (prevDevice ? { ...prevDevice, ...parsedMessage } : parsedMessage));
          updateRecord(mac, parsedMessage).catch((error) => console.error('Error updating record:', error));
        }
      } catch (error) {
        console.error('Error parsing MQTT message:', error);
      }
    }
  }, [mqttMessage, mac]);

  const handleSwitchToggle = (index: number) => {
    if (!device || !device.out) return;
    const newValue = !device.out[index];
    const fullMessage = {
      command: "setOutput",
      mac: device.mac,
      portNo: index,
      value: newValue ? 1 : 0
    };
    const topic = `i2r/${email}/in`;
    MQTTService.getInstance().publish(topic, JSON.stringify(fullMessage));

    setDevice((prevDevice) => {
      if (!prevDevice) return null;
      const updatedOut = [...(prevDevice.out || [])];
      updatedOut[index] = newValue ? 1 : 0;
      return { ...prevDevice, out: updatedOut };
    });
  };

  const handleSave = async () => {
    if (device) {
      try {
        const response = await updateName(device.mac, editedNames);
        setDevice({ ...device, name: editedNames });
        setEditMode(false);
      } catch (error) {
        console.error('Error updating record:', error);
      }
    }
  };

  const handleCancel = () => {
    setEditedNames(device?.name || []);
    setEditMode(false);
  };

  const handleNameChange = (index: number, value: string) => {
    const updatedNames = [...editedNames];
    updatedNames[index] = value;
    setEditedNames(updatedNames);
  };

  // Modal Handlers
  const handleOpenModal = (index: number) => {
    setModalTargetIndex(index);
    setIsModalOpen(true);
  };

  const handleSwitchLabelClick = (index: number) => {
    if (device?.name) {
      const switchLabelIndex = index + 1 + (device.in?.length || 0);
      setEditedSwitchLabel(device.name[switchLabelIndex] || `스위치 ${index + 1}`);
    } else {
      setEditedSwitchLabel('');
    }
    setSelectedSwitchIndex(index);
    setIsSwitchModalOpen(true);
  };

  const handleOpenMeasureModal = (type: { key: string; label: string }) => {
    setModalState({ isOpen: true, measurement: type });
  };

  const handleCloseMeasureModal = () => {
    setModalState({ isOpen: false });
  };

  const handleFirmwareDownload = () => {
    if (!device || !device.mac) return;

    // device.type 혹은 device.t (단축키) 사용
    const typeVal = device.type || device.t;
    let fileName = '';

    // 만약 "i2r-03" 처럼 이미 전체 이름이 문자열로 오면 그대로 사용
    if (typeof typeVal === 'string' && typeVal.startsWith('i2r-')) {
      fileName = `${typeVal}.ino.bin`;
    } else {
      // 기존 숫자(3) 혹은 "3" 등이 오면 접두어 붙이기
      fileName = `i2r-0${typeVal}.ino.bin`;
    }

    const message = {
      mac: device.mac,
      command: 'downloadFirmware',
      email: email,
      fileName: fileName
    };
    const topic = `i2r/${email}/in`;
    MQTTService.getInstance().publish(topic, JSON.stringify(message));
    alert(`펌웨어 다운로드 명령을 전송했습니다.\n파일: ${fileName}`);
  };


  if (!device) return <div className="device-detail-container">Loading...</div>;

  return (
    <div className="device-detail-container">
      {/* Header */}
      <div className="dashboard-header">
        <div className="device-title-area">
          <h2>
            {device.name && device.name[0] ? device.name[0] : 'Unnamed Device'}
            <FaCog className="edit-icon" onClick={() => setEditMode(true)} />
          </h2>
          <span style={{ color: '#64748b', fontSize: '0.9rem' }}>MAC: {device.mac}</span>
        </div>
        <button onClick={handleFirmwareDownload} className="action-btn btn-primary">
          <FaDownload /> 펌웨어 다운로드
        </button>
      </div>

      {editMode && (
        <div className="detail-card" style={{ marginBottom: '24px' }}>
          <h3>이름 변경</h3>
          <div className="edit-names-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            {editedNames.map((name, index) => {
              let label;
              if (index === 0) label = "메인 기기 이름";
              else if (index <= (device?.in?.length || 0)) label = `입력 ${index}`;
              else {
                const outIndex = index - (device?.in?.length || 0);
                label = `출력 ${outIndex}`;
              }
              return (
                <div key={index} style={{ marginBottom: '10px' }}>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: '#666' }}>{label}</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => handleNameChange(index, e.target.value)}
                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }}
                  />
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
            <button onClick={handleSave} className="action-btn btn-primary">저장</button>
            <button onClick={handleCancel} className="action-btn btn-secondary">취소</button>
          </div>
        </div>
      )}

      {/* 1. Input Status (Top) - Reordered */}
      <div className="detail-card status-panel" style={{ marginBottom: '24px' }}>
        <div className="card-title"><FaMicrochip /> 입력 상태 (Inputs)</div>
        <div className="status-grid-row">
          {device.in?.map((state, index) => (
            <div key={`led-${index}`} className="status-item-col">
              <span className="status-name" onClick={() => handleOpenModal(index)}>
                {device.name?.[index + 1] || `입력 ${index + 1}`}
              </span>
              <div className={`status-indicator ${state === 1 ? 'status-on' : 'status-off'}`}></div>
            </div>
          ))}
          {(!device.in || device.in.length === 0) && <p style={{ color: '#999' }}>입력 포트 없음</p>}
        </div>
      </div>

      {/* 2. Output Controls (Middle) - Reordered */}
      <div className="detail-card control-panel" style={{ marginBottom: '24px' }}>
        <div className="card-title"><FaBolt /> 출력 제어 (Outputs)</div>
        <div className="switches-grid">
          {device.out?.map((state, index) => (
            <div key={`switch-${index}`} className="switch-item">
              <span className="switch-name" onClick={() => handleSwitchLabelClick(index)}>
                {device.name && device.name[index + 1 + (device.in?.length || 0)]
                  ? device.name[index + 1 + (device.in?.length || 0)]
                  : `스위치 ${index + 1}`}
              </span>
              <button
                className={`switch-toggle ${state === 1 ? 'active' : ''}`}
                onClick={() => handleSwitchToggle(index)}
              ></button>
            </div>
          ))}
          {(!device.out || device.out.length === 0) && <p style={{ color: '#999' }}>출력 포트 없음</p>}
        </div>
      </div>

      {/* 3. Sensor Grid (Bottom) - Reordered */}
      <div className="sensor-section">
        {device['temperature'] !== undefined && (
          <div className="sensor-card" onClick={() => handleOpenMeasureModal({ key: 'temperature', label: '온도' })}>
            <div className="sensor-icon-wrapper" style={{ backgroundColor: '#fee2e2', color: '#ef4444' }}><FaTemp /></div>
            <div className="sensor-info">
              <span className="sensor-value">{device['temperature']}<span style={{ fontSize: '1rem', fontWeight: 500, marginLeft: '4px' }}>°C</span></span>
              <span className="sensor-label">온도</span>
            </div>
          </div>
        )}
        {device['humidity'] !== undefined && (
          <div className="sensor-card" onClick={() => handleOpenMeasureModal({ key: 'humidity', label: '습도' })}>
            <div className="sensor-icon-wrapper" style={{ backgroundColor: '#e0f2fe', color: '#0ea5e9' }}><FaHumi /></div>
            <div className="sensor-info">
              <span className="sensor-value">{device['humidity']}<span style={{ fontSize: '1rem', fontWeight: 500, marginLeft: '4px' }}>%</span></span>
              <span className="sensor-label">습도</span>
            </div>
          </div>
        )}
        {device['light'] !== undefined && (
          <div className="sensor-card" onClick={() => handleOpenMeasureModal({ key: 'light', label: '조도' })}>
            <div className="sensor-icon-wrapper" style={{ backgroundColor: '#fef9c3', color: '#eab308' }}><FaBolt /></div>
            <div className="sensor-info">
              <span className="sensor-value">{device['light']}<span style={{ fontSize: '1rem', fontWeight: 500, marginLeft: '4px' }}>lx</span></span>
              <span className="sensor-label">조도</span>
            </div>
          </div>
        )}
        {device['analog'] !== undefined && (
          <div className="sensor-card" onClick={() => handleOpenMeasureModal({ key: 'analog', label: 'Analog' })}>
            <div className="sensor-icon-wrapper" style={{ backgroundColor: '#f3f4f6', color: '#64748b' }}><FaBolt /></div>
            <div className="sensor-info">
              <span className="sensor-value">{device['analog']}<span style={{ fontSize: '1rem', fontWeight: 500, marginLeft: '4px' }}></span></span>
              <span className="sensor-label">Analog</span>
            </div>
          </div>
        )}
        {device['carbon'] !== undefined && (
          <div className="sensor-card" onClick={() => handleOpenMeasureModal({ key: 'carbon', label: 'CO2' })}>
            <div className="sensor-icon-wrapper" style={{ backgroundColor: '#d1fae5', color: '#10b981' }}><FaMicrochip /></div>
            <div className="sensor-info">
              <span className="sensor-value">{device['carbon']}<span style={{ fontSize: '1rem', fontWeight: 500, marginLeft: '4px' }}>ppm</span></span>
              <span className="sensor-label">CO2</span>
            </div>
          </div>
        )}
        {device['voltAdc'] !== undefined && (
          <div className="sensor-card" onClick={() => handleOpenMeasureModal({ key: 'voltAdc', label: '전압(ADC)' })}>
            <div className="sensor-icon-wrapper" style={{ backgroundColor: '#ede9fe', color: '#8b5cf6' }}><FaBolt /></div>
            <div className="sensor-info">
              <span className="sensor-value">{device['voltAdc']}<span style={{ fontSize: '1rem', fontWeight: 500, marginLeft: '4px' }}>V</span></span>
              <span className="sensor-label">전압(ADC)</span>
            </div>
          </div>
        )}
        {device['voltBat'] !== undefined && (
          <div className="sensor-card" onClick={() => handleOpenMeasureModal({ key: 'voltBat', label: '배터리' })}>
            <div className="sensor-icon-wrapper" style={{ backgroundColor: '#fae8ff', color: '#d946ef' }}><FaBolt /></div>
            <div className="sensor-info">
              <span className="sensor-value">{device['voltBat']}<span style={{ fontSize: '1rem', fontWeight: 500, marginLeft: '4px' }}>V</span></span>
              <span className="sensor-label">배터리</span>
            </div>
          </div>
        )}
      </div>


      {/* Footer Links (Full Width) */}
      <div className="footer-links" style={{ marginTop: '40px' }}>
        <a href="https://youtu.be/cM9QOf1bDiU" target="_blank" rel="noopener noreferrer" className="action-btn btn-secondary">
          <FaYoutube style={{ color: '#FF0000', fontSize: '1.2rem' }} /> 이름설정
        </a>
        <a href="https://youtu.be/hlbH4OiEDu4" target="_blank" rel="noopener noreferrer" className="action-btn btn-secondary">
          <FaYoutube style={{ color: '#FF0000', fontSize: '1.2rem' }} /> 타이머설정
        </a>
        <a href="https://youtu.be/e6tqkVcQ8n0" target="_blank" rel="noopener noreferrer" className="action-btn btn-secondary">
          <FaYoutube style={{ color: '#FF0000', fontSize: '1.2rem' }} /> 입력트리거
        </a>
        <a href="https://youtu.be/hOzujluNKDU" target="_blank" rel="noopener noreferrer" className="action-btn btn-secondary">
          <FaYoutube style={{ color: '#FF0000', fontSize: '1.2rem' }} /> 센서트리거
        </a>
      </div>


      {/* Modals */}
      {isModalOpen && modalTargetIndex !== null && (
        <ModalLed
          mqttMessage={mqttMessage}
          onCancel={() => setIsModalOpen(false)}
          labelNo={modalTargetIndex}
          deviceMac={mac || 'Unknown MAC'}
          name={device.name || []}
        />
      )}

      {isSwitchModalOpen && selectedSwitchIndex !== null && (
        <ModalSwitch
          mqttMessage={mqttMessage}
          deviceMac={mac || 'Unknown MAC'}
          onCancel={() => setIsSwitchModalOpen(false)}
          labelNo={selectedSwitchIndex}
          currentLabel={editedSwitchLabel}
        />
      )}

      {modalState.isOpen && modalState.measurement && (
        <ModalMeasure
          typeSensor={modalState.measurement.key}
          value={device[modalState.measurement.key]}
          label={modalState.measurement.label}
          deviceMac={device.mac}
          mqttMessage={mqttMessage}
          onCancel={handleCloseMeasureModal}
        />
      )}
    </div>
  );
};

export default DeviceDetail;
